import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { BrowserWindow, Experiments, IPropsStore, TpaHandlerProvider } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	ExperimentsSymbol,
	PageFeatureConfigSymbol,
	Props,
	SiteFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import type { TpaCommonsSiteConfig } from 'feature-tpa-commons'
import { name as tpaCommonsName } from 'feature-tpa-commons'
import { name } from '../symbols'
import { PERMITTED_FULL_SCREEN_TPAS_IN_MOBILE } from '../utils/constants'
import type { IMobileFullScreenModeApi } from 'feature-mobile-full-screen'
import { TpaFullScreenModeAPISymbol } from 'feature-mobile-full-screen'
import type { TpaPageConfig } from '../types'
import { getIOSVersion } from '@wix/thunderbolt-commons'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export type MessageData = {
	isFullScreen: boolean
}

type PropsToUpdate = {
	isMobileFullScreenMode: boolean
	iOSVersion?: number | null
	isMobileView?: boolean
}

export const SetFullScreenMobileHandler = withDependencies(
	[
		named(SiteFeatureConfigSymbol, tpaCommonsName),
		named(PageFeatureConfigSymbol, name),
		Props,
		BrowserWindowSymbol,
		TpaFullScreenModeAPISymbol,
		ExperimentsSymbol,
	],
	(
		siteConfig: TpaCommonsSiteConfig,
		{ widgets }: TpaPageConfig,
		props: IPropsStore,
		window: BrowserWindow,
		mobileFullScreenApi: IMobileFullScreenModeApi,
		experiments: Experiments
	): TpaHandlerProvider => {
		const isComponentAllowedInFullScreenMode = (compId: string): boolean => {
			const { widgetsClientSpecMapData } = siteConfig
			const widget: any = widgets[compId] || {}
			const { appDefinitionId } = widgetsClientSpecMapData[widget.widgetId] || {}
			return Object.values(PERMITTED_FULL_SCREEN_TPAS_IN_MOBILE).includes(appDefinitionId)
		}

		return {
			getTpaHandlers() {
				return {
					async setFullScreenMobile(compId: string, { isFullScreen }: MessageData) {
						const { isMobileView, viewMode } = siteConfig
						if (!isMobileView) {
							throw new Error('show full screen is only available in Mobile view')
						}

						const updateTpaProps = (propsToUpdate: PropsToUpdate) => {
							props.update({
								[compId]: propsToUpdate,
							})
						}

						const isUpdateTpaHandlerInPreviewEnabled = isExperimentOpen(
							experiments,
							'specs.thunderbolt.updateTpaHandlerMobileViewInPreviewMode'
						)

						if (isComponentAllowedInFullScreenMode(compId)) {
							const isInPreviewMode = viewMode === 'preview' && isUpdateTpaHandlerInPreviewEnabled
							if (isFullScreen) {
								updateTpaProps({
									iOSVersion: getIOSVersion(window!),
									isMobileFullScreenMode: true,
								})
							} else if (isInPreviewMode) {
								updateTpaProps({
									isMobileFullScreenMode: true,
									isMobileView,
								})
							} else {
								updateTpaProps({
									isMobileFullScreenMode: false,
								})
							}
							mobileFullScreenApi.setFullScreenMobile(compId, isFullScreen)
						}
					},
				}
			},
		}
	}
)
